import { Link, useLocation } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { canAccessRoute } from '@/lib/roles'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/authStore'
import { useBetaPendingCount } from '@/hooks/useBetaRequests'
import { useFeedbackNewCount } from '@/hooks/useFeedback'
import { useConfigRequestsPendingCount } from '@/hooks/useConfigRequests'
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Ticket,
  BarChart3,
  Settings,
  FileText,
  FileCode,
  ClipboardList,
  ClipboardCheck,
  Zap,
  Rocket,
  Puzzle,
  Shuffle,
  Package,
  Mail,
  MessageSquareMore,
  Send,
  Gauge,
  Megaphone,
  Database,
  SlidersHorizontal,
} from 'lucide-react'

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/users', icon: Users, label: 'Usuarios' },
  { to: '/beta-requests', icon: ClipboardCheck, label: 'Solicitudes Beta', hasBadge: true },
  { to: '/feedback', icon: MessageSquareMore, label: 'Feedback', hasBadge: true },
  {
    to: '/client-config',
    icon: SlidersHorizontal,
    label: 'Config. de Clientes',
    hasBadge: true,
  },
  { to: '/plans', icon: CreditCard, label: 'Planes' },
  { to: '/packages', icon: Package, label: 'Paquetes' },
  { to: '/coupons', icon: Ticket, label: 'Cupones' },
  { to: '/credit-costs', icon: Zap, label: 'Creditos' },
  { to: '/boosts', icon: Rocket, label: 'Boosts' },
  { to: '/addons', icon: Puzzle, label: 'Add-ons' },
  { to: '/knowledge-bases', icon: Database, label: 'Bases de conocimiento' },
  { to: '/distribution-modes', icon: Shuffle, label: 'Distribucion' },
  { to: '/templates', icon: FileCode, label: 'Plantillas PDF' },
  { to: '/email-templates', icon: Mail, label: 'Plantillas Email' },
  { to: '/email-campaigns', icon: Send, label: 'Campañas Email' },
  { to: '/landing-pages', icon: Megaphone, label: 'Landing Pages' },
  { to: '/revenue', icon: FileText, label: 'Ingresos' },
  { to: '/rapidapi', icon: Gauge, label: 'RapidAPI' },
  { to: '/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/settings', icon: Settings, label: 'Configuracion' },
  { to: '/audit', icon: ClipboardList, label: 'Audit Log' },
]

export const Sidebar = () => {
  const location = useLocation()
  const role = useAuthStore((s) => s.user?.role)
  const { data: pendingCount } = useBetaPendingCount()
  const { data: feedbackCount } = useFeedbackNewCount()
  const { data: configRequestsCount } = useConfigRequestsPendingCount()
  const betaPending = pendingCount?.count || 0
  const feedbackNew = feedbackCount?.count || 0
  const configRequestsPending = configRequestsCount?.count || 0

  const getBadgeCount = (to: string) => {
    if (to === '/beta-requests') return betaPending
    if (to === '/feedback') return feedbackNew
    if (to === '/client-config') return configRequestsPending
    return 0
  }

  const visibleItems = navItems.filter((item) => canAccessRoute(role, item.to))

  return (
    <aside className="w-64 border-r bg-card">
      <div className="flex h-16 items-center border-b px-6">
        <Link to="/" className="flex items-center gap-2 text-lg font-bold">
          <span className="text-primary">Uyari</span>
          <span className="text-muted-foreground">Admin</span>
        </Link>
      </div>
      <nav className="space-y-1 p-4">
        {visibleItems.map((item) => {
          const Icon = item.icon
          const isActive = location.pathname === item.to
          const badgeCount = 'hasBadge' in item && item.hasBadge ? getBadgeCount(item.to) : 0
          const showBadge = badgeCount > 0

          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
              <span className="flex-1">{item.label}</span>
              {showBadge && (
                <Badge variant="error" className="h-5 min-w-5 px-1 text-xs">
                  {badgeCount}
                </Badge>
              )}
            </Link>
          )
        })}
      </nav>
    </aside>
  )
}
